/**
 * Wholesale Quantity Helpers
 * Handles conversion between cases and units for wholesale business logic
 * Database stores units, customers see cases
 */

/**
 * Convert cases to units
 * @param {number} cases - Number of cases
 * @param {number} unitsPerCase - Units per case
 * @returns {number} Total units
 */
export const casesToUnits = (cases, unitsPerCase) => {
  if (!cases || !unitsPerCase) return 0;
  return Math.floor(cases) * Math.floor(unitsPerCase);
};

/**
 * Convert units to cases (with remainder)
 * @param {number} units - Total units
 * @param {number} unitsPerCase - Units per case
 * @returns {object} {cases, remainderUnits}
 */
export const unitsToCases = (units, unitsPerCase) => {
  if (!units || !unitsPerCase) return { cases: 0, remainderUnits: 0 };
  
  const totalUnits = Math.floor(units);
  const unitsPerCaseNum = Math.floor(unitsPerCase);
  
  const cases = Math.floor(totalUnits / unitsPerCaseNum);
  const remainderUnits = totalUnits % unitsPerCaseNum;
  
  return { cases, remainderUnits };
};

/**
 * Calculate total units from cases and individual units
 * @param {number} cases - Number of cases
 * @param {number} singleUnits - Individual units
 * @param {number} unitsPerCase - Units per case
 * @returns {number} Total units
 */
export const calculateTotalUnits = (cases, singleUnits, unitsPerCase) => {
  const caseUnits = casesToUnits(cases || 0, unitsPerCase || 0);
  return caseUnits + (singleUnits || 0);
};

/**
 * Calculate case pricing
 * @param {number} unitPrice - Price per unit
 * @param {number} unitsPerCase - Units per case
 * @returns {number} Price per case
 */
export const calculateCasePrice = (unitPrice, unitsPerCase) => {
  if (!unitPrice || !unitsPerCase) return 0;
  return unitPrice * unitsPerCase;
};

/**
 * Whether the stock a customer can still add is below one full case. Drives the "Create Back
 * Order" gate — customers can only start a back order once there isn't a full case left to buy.
 * Subtracts what's already in the cart from available stock (adding 3 of 3.5 cases leaves <1 case,
 * so the button appears). Falls back to a case size of 1 (sold-by-unit) so a product is "below a
 * case" only when nothing remains.
 * @param {object} product - needs unitAvailable + unitsPerCase
 * @param {number} [unitsInCart=0] - units of this product already in the cart
 * @returns {boolean}
 */
export const isBelowOneCase = (product, unitsInCart = 0) => {
  const available = Number(product?.unitAvailable) || 0;
  const inCart = Number(unitsInCart) || 0;
  const perCase = Number(product?.unitsPerCase) || 1;
  return available - inCart < perCase;
};

/**
 * Calculate line total for cart item
 * @param {object} item - Cart item with quantity, singleUnitQuantity, Price, UnitPrice, unitPurchasableFee
 * @returns {number} Line total
 */
export const calculateLineTotal = (item) => {
  if (!item) return 0;
  
  const caseTotal = (item.quantity || 0) * (item.Price || 0);
  const unitTotal = (item.singleUnitQuantity || 0) * ((item.UnitPrice || 0) + (item.unitPurchasableFee || 0));
  
  return caseTotal + unitTotal;
};

/**
 * Format quantity display for UI
 * @param {number} cases - Number of cases
 * @param {number} singleUnits - Individual units
 * @param {number} unitsPerCase - Units per case
 * @returns {string} Formatted quantity string
 */
export const formatQuantityDisplay = (cases, singleUnits, unitsPerCase) => {
  const parts = [];
  
  if (cases > 0) {
    parts.push(`${cases} case${cases === 1 ? '' : 's'}`);
  }
  
  if (singleUnits > 0) {
    parts.push(`${singleUnits} unit${singleUnits === 1 ? '' : 's'}`);
  }
  
  if (parts.length === 0) {
    return '0 units';
  }
  
  const total = calculateTotalUnits(cases, singleUnits, unitsPerCase);
  return `${parts.join(' + ')} (${total} total units)`;
};

/**
 * Validate quantity constraints
 * @param {number} cases - Number of cases
 * @param {number} singleUnits - Individual units
 * @param {number} unitsPerCase - Units per case
 * @param {number} maxUnits - Maximum available units
 * @returns {object} {isValid, error, totalUnits}
 */
export const validateQuantity = (cases, singleUnits, unitsPerCase, maxUnits) => {
  const totalUnits = calculateTotalUnits(cases || 0, singleUnits || 0, unitsPerCase || 0);
  
  if (totalUnits === 0) {
    return {
      isValid: false,
      error: 'Quantity must be greater than 0',
      totalUnits
    };
  }
  
  if (totalUnits > maxUnits) {
    return {
      isValid: false,
      error: `Quantity exceeds available stock (${maxUnits} units available)`,
      totalUnits
    };
  }
  
  if (singleUnits >= unitsPerCase) {
    return {
      isValid: false,
      error: `Single units must be less than ${unitsPerCase} (order full cases instead)`,
      totalUnits
    };
  }
  
  return {
    isValid: true,
    error: null,
    totalUnits
  };
};

/**
 * Cart-specific quantity helpers
 */
export const cartQuantityHelpers = {
  /**
   * Update case quantity in cart item
   */
  updateCaseQuantity: (item, newCaseQuantity, maxUnits) => {
    const cases = Math.max(0, Math.floor(newCaseQuantity || 0));
    const validation = validateQuantity(cases, item.singleUnitQuantity || 0, item.UnitsPerCase, maxUnits);
    
    if (!validation.isValid) {
      return { success: false, error: validation.error };
    }
    
    return {
      success: true,
      updatedItem: {
        ...item,
        quantity: cases
      }
    };
  },

  /**
   * Update single unit quantity in cart item
   */
  updateSingleUnitQuantity: (item, newSingleUnitQuantity, maxUnits) => {
    const singleUnits = Math.max(0, Math.floor(newSingleUnitQuantity || 0));
    const validation = validateQuantity(item.quantity || 0, singleUnits, item.UnitsPerCase, maxUnits);
    
    if (!validation.isValid) {
      return { success: false, error: validation.error };
    }
    
    return {
      success: true,
      updatedItem: {
        ...item,
        singleUnitQuantity: singleUnits
      }
    };
  },

  /**
   * Calculate cart item total quantity in units
   */
  getItemTotalUnits: (item) => {
    return calculateTotalUnits(
      item.quantity || 0,
      item.singleUnitQuantity || 0,
      item.UnitsPerCase || 1
    );
  },

  /**
   * Calculate cart item subtotal
   */
  getItemSubtotal: (item) => {
    return calculateLineTotal(item);
  },

  /**
   * Get cart item display text
   */
  getItemQuantityText: (item) => {
    return formatQuantityDisplay(
      item.quantity || 0,
      item.singleUnitQuantity || 0,
      item.UnitsPerCase || 1
    );
  }
};

/**
 * Product-specific quantity helpers
 */
export const productQuantityHelpers = {
  /**
   * Calculate minimum order quantity
   */
  getMinOrderQuantity: (product) => {
    return product.MinimumOrderQuantity || 1;
  },

  /**
   * Get maximum orderable quantity
   */
  getMaxOrderQuantity: (product) => {
    return Math.min(
      product.QuantityAvailable || 0,
      product.UnitAvailable || 0
    );
  },

  /**
   * Check if product is in stock
   */
  isInStock: (product) => {
    return (product.QuantityAvailable || 0) > 0 && (product.UnitAvailable || 0) > 0;
  },

  /**
   * Get stock status message
   */
  getStockStatus: (product) => {
    const maxQuantity = productQuantityHelpers.getMaxOrderQuantity(product);
    
    if (maxQuantity === 0) {
      return { status: 'out-of-stock', message: 'Out of Stock' };
    } else if (maxQuantity <= (product.AlertLevel || 10)) {
      return { status: 'low-stock', message: `Low Stock (${maxQuantity} left)` };
    } else {
      return { status: 'in-stock', message: 'In Stock' };
    }
  }
};

/**
 * Currency formatting helper
 */
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount || 0);
};

/**
 * Number formatting helper
 */
export const formatNumber = (number, decimals = 0) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(number || 0);
};